/**
 * Service Factory
 * Centralized factory for creating and managing service instances
 * Provides dependency injection and service lifecycle management
 */

import { apiClient } from './apiClient';
import { graphqlClient } from './graphqlClient';

export interface ServiceContext {
  userId?: string | number;
  hasuraToken?: string;
  userType?: string;
  token?: string;
}

export interface BaseService {
  readonly context: ServiceContext;
  updateContext(context: Partial<ServiceContext>): void;
}

/**
 * Abstract base service class that all services should extend
 */
export abstract class AbstractService implements BaseService {
  protected apiClient = apiClient;
  protected graphqlClient = graphqlClient;
  
  constructor(public readonly context: ServiceContext = {}) {}

  updateContext(newContext: Partial<ServiceContext>): void {
    Object.assign(this.context, newContext);
  }

  protected requireAuth(): void {
    if (!this.context.hasuraToken) {
      throw new Error('Authentication token is required for this operation');
    }
  }

  protected requireUserId(): void {
    if (!this.context.userId) {
      throw new Error('User ID is required for this operation');
    }
  }

  protected buildPepEndpoint(path: string): string {
    this.requireUserId();
    return this.apiClient.buildPepEndpoint(this.context.userId!, path);
  }
}

/**
 * Service factory for creating service instances with proper context
 */
class ServiceFactory {
  private services: Map<string, BaseService> = new Map();
  private globalContext: ServiceContext = {};

  /**
   * Set global context that will be applied to all services
   */
  setGlobalContext(context: ServiceContext): void {
    this.globalContext = { ...context };
    
    // Update existing service instances
    this.services.forEach(service => {
      service.updateContext(this.globalContext);
    });
  }

  /**
   * Update global context partially
   */
  updateGlobalContext(context: Partial<ServiceContext>): void {
    Object.assign(this.globalContext, context);
    
    // Update existing service instances
    this.services.forEach(service => {
      service.updateContext(context);
    });
  }

  /**
   * Get or create a service instance
   */
  getService<T extends BaseService>(
    ServiceClass: new (context: ServiceContext) => T,
    key?: string
  ): T {
    const serviceKey = key || ServiceClass.name;
    
    if (!this.services.has(serviceKey)) {
      const service = new ServiceClass({ ...this.globalContext });
      this.services.set(serviceKey, service);
    }
    
    return this.services.get(serviceKey) as T;
  }

  /**
   * Create a new service instance with specific context
   */
  createService<T extends BaseService>(
    ServiceClass: new (context: ServiceContext) => T,
    context: Partial<ServiceContext> = {}
  ): T {
    const mergedContext = { ...this.globalContext, ...context };
    return new ServiceClass(mergedContext);
  }

  /**
   * Clear all cached service instances
   */
  clearServices(): void {
    this.services.clear();
  }

  /**
   * Get current global context
   */
  getGlobalContext(): ServiceContext {
    return { ...this.globalContext };
  }
}

// Export singleton instance
export const serviceFactory = new ServiceFactory();

// Export types and classes
export { apiClient, graphqlClient };
export default serviceFactory;